<template>
  <div class="create-booking-container">
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <!-- Header Section -->
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <v-btn
                  icon
                  variant="text"
                  color="white"
                  size="small"
                  @click="goBack"
                >
                  <v-icon size="20">mdi-arrow-left</v-icon>
                </v-btn>
                
                <div class="ml-3">
                  <div class="text-h6 font-weight-semibold" style="color: #ffffff;">
                    {{ isNew ? $t('booking.create-booking') : $t('booking.edit-booking') }}
                  </div>
                </div>

                <v-spacer />

                <v-btn
                  variant="flat"
                  :color="canSubmit ? '#28A745' : '#DC3545'"
                  rounded="lg"
                  class="text-none font-weight-semibold"
                  :disabled="!canSubmit || isSaving"
                  :loading="isSaving"
                  @click="isNew ? submitBooking() : editBooking()"
                >
                  <v-icon start size="18">{{ isNew ? 'mdi-check-circle' : 'mdi-content-save' }}</v-icon>
                  {{ isNew ? $t('office.submit') : $t('booking.save-changes') }}
                </v-btn>
              </div>
            </v-card>
          </v-sheet>

          <!-- Main Content -->
          <v-sheet color="transparent" class="mt-4">
            <v-card color="white" elevation="2" rounded="xl" class="pa-4 pa-md-6">
              <!-- Existing Reservations Alert -->
              <v-alert
                v-if="existingReservations.length > 0"
                type="error"
                variant="tonal"
                class="mb-4"
                rounded="lg"
                prominent
              >
                <template #prepend>
                  <v-icon size="28">mdi-alert-circle</v-icon>
                </template>
                <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between">
                  <div>
                    <div class="text-subtitle-1 font-weight-semibold mb-1">{{ $t('office.existing-reservation') }}</div>
                    <div class="text-body-2">{{ $t('booking.time-slot-occupied') }}</div>
                  </div>
                  <v-btn
                    variant="outlined"
                    color="error"
                    rounded="lg"
                    class="text-none mt-3 mt-sm-0"
                    @click="showExistingDialog = true"
                  >
                    <v-icon start size="18">mdi-calendar-clock</v-icon>
                    {{ $t('office.show') }}
                  </v-btn>
                </div>
              </v-alert>

              <v-form ref="formRef" @submit.prevent>
                <v-row>
                  <!-- Location Info Card -->
                  <v-col cols="12">
                    <v-card
                      rounded="xl"
                      elevation="0"
                      class="pa-4"
                      style="background: linear-gradient(135deg, #F3F6FF 0%, #FFFFFF 100%); border: 1px solid #E2E8F0;"
                    >
                      <div class="text-caption font-weight-bold mb-3" style="color: #718096; text-transform: uppercase; letter-spacing: 0.05em;">
                        {{ $t('booking.location-details') }}
                      </div>
                      <v-row dense>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <v-icon size="16" color="#23A5E6">mdi-map-marker</v-icon>
                            <div class="info-content">
                              <div class="info-label">{{ $t('office.location') }}</div>
                              <div class="info-value">{{ reservation.poi.name }}</div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <v-icon size="16" color="#23A5E6">mdi-tag</v-icon>
                            <div class="info-content">
                              <div class="info-label">{{ $t('office.category') }}</div>
                              <div class="info-value">{{ reservation.category.name }}</div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <v-icon size="16" color="#23A5E6">mdi-stairs</v-icon>
                            <div class="info-content">
                              <div class="info-label">{{ $t('office.floor') }}</div>
                              <div class="info-value">{{ reservation.floor.name }}</div>
                            </div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                          <div class="info-item">
                            <v-icon size="16" color="#23A5E6">mdi-office-building</v-icon>
                            <div class="info-content">
                              <div class="info-label">{{ $t('office.building') }}</div>
                              <div class="info-value">{{ reservation.building.name }}</div>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>

                  <!-- Date & Time Selection -->
                  <v-col cols="12">
                    <v-card
                      rounded="xl"
                      class="date-time-card"
                      elevation="0"
                      @click="datePickerDialog = true"
                    >
                      <div class="date-time-header">
                        <v-icon color="#285096" size="24">mdi-calendar-clock</v-icon>
                        <span class="date-time-label">{{ $t('office.date-time') }}</span>
                      </div>
                      <div class="date-time-content">
                        <div class="date-time-row">
                          <div class="date-time-item">
                            <div class="date-time-icon-wrapper start">
                              <v-icon size="16">mdi-calendar-start</v-icon>
                            </div>
                            <div class="date-time-info">
                              <div class="date-time-sub-label">{{ $t('booking.start') }}</div>
                              <div class="date-time-value">{{ moment(reservation.range.start).format('MMM DD, YYYY') }}</div>
                              <div class="date-time-sub-value">{{ moment(reservation.range.start).format('HH:mm') }}</div>
                            </div>
                          </div>

                          <v-icon class="date-arrow" color="#9AA6B1">mdi-arrow-right</v-icon>

                          <div class="date-time-item">
                            <div class="date-time-icon-wrapper end">
                              <v-icon size="16">mdi-calendar-end</v-icon>
                            </div>
                            <div class="date-time-info">
                              <div class="date-time-sub-label">{{ $t('booking.end') }}</div>
                              <div class="date-time-value">{{ moment(reservation.range.end).format('MMM DD, YYYY') }}</div>
                              <div class="date-time-sub-value">{{ moment(reservation.range.end).format('HH:mm') }}</div>
                            </div>
                          </div>
                        </div>

                        <div v-if="isBatchReservation" class="batch-indicator">
                          <v-chip
                            size="small"
                            variant="tonal"
                            color="#23A5E6"
                            prepend-icon="mdi-calendar-multiple"
                          >
                            {{ $t('booking.multi-day-booking') }}
                          </v-chip>
                        </div>
                      </div>
                    </v-card>
                  </v-col>

                  <!-- Subject -->
                  <v-col cols="12">
                    <v-text-field
                      v-model="reservation.title"
                      :label="$t('booking.subject') + ' *'"
                      variant="outlined"
                      density="comfortable"
                      rounded="lg"
                      prepend-inner-icon="mdi-format-title"
                      color="#23A5E6"
                      maxlength="50"
                      counter
                      :rules="[rules.required, rules.minLength]"
                    />
                  </v-col>

                  <!-- Creator Information -->
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="reservation.creatorName"
                      :label="$t('office.creator-name')"
                      variant="outlined"
                      density="comfortable"
                      rounded="lg"
                      prepend-inner-icon="mdi-account"
                      color="#23A5E6"
                      readonly
                      class="readonly-field"
                    />
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="reservation.creatorEmail"
                      :label="$t('office.creator-email')"
                      variant="outlined"
                      density="comfortable"
                      rounded="lg"
                      prepend-inner-icon="mdi-email"
                      color="#23A5E6"
                      readonly
                      class="readonly-field"
                    />
                  </v-col>

                  <!-- Description -->
                  <v-col cols="12">
                    <v-textarea
                      v-model="reservation.description"
                      :label="$t('office.description')"
                      variant="outlined"
                      density="comfortable"
                      rounded="lg"
                      prepend-inner-icon="mdi-text"
                      color="#23A5E6"
                      rows="4"
                      maxlength="200"
                      counter
                      auto-grow
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <!-- Date Picker Dialog -->
    <v-dialog
      v-model="datePickerDialog"
      max-width="600"
      persistent
      transition="dialog-bottom-transition"
    >
      <v-card rounded="xl" class="date-picker-dialog-card">
        <v-card-title
          class="pa-4 d-flex align-center"
          style="background: linear-gradient(135deg, #285096 0%, #23A5E6 100%);"
        >
          <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
          <span class="text-h6 font-weight-semibold" style="color: white;">
            {{ $t('booking.select-date-time') }}
          </span>
          <v-spacer />
          <v-btn icon variant="text" color="white" @click="discardRange">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-0">
          <v-alert
            v-if="timeError"
            type="error"
            variant="tonal"
            class="ma-4"
            rounded="lg"
          >
            {{ timeError }}
          </v-alert>

          <div class="date-picker-wrapper">
            <DatePicker
              v-if="showDatePicker"
              v-model.range="tempRange"
              is-range
              mode="dateTime"
              :rules="timeRules"
              :disabled-dates="disabledDates"
              :min-date="new Date()"
              :max-date="moment().add(59, 'days').toDate()"
              is24hr
              :is-dark="false"
              color="#23A5E6"
              @dayclick="(day, event) => { onDayClick(day); event.target.blur(); }"
              @update:modelValue="validateTimeRange"
            />
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4" style="background: #F8FAFC;">
          <v-btn
            variant="text"
            color="#4E5965"
            rounded="lg"
            class="text-none"
            @click="discardRange"
          >
            {{ $t('booking.cancel') }}
          </v-btn>

          <v-spacer />

          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none px-6"
            :disabled="isRangeEqual || !!timeError"
            @click="confirmRange"
          >
            <v-icon start size="18">mdi-check</v-icon>
            {{ $t('booking.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Existing Reservations Dialog -->
    <v-dialog
      v-model="showExistingDialog"
      max-width="600"
      scrollable
    >
      <v-card rounded="xl">
        <v-card-title
          class="pa-4 d-flex align-center"
          style="background: linear-gradient(135deg, #DC3545 0%, #E04747 100%);"
        >
          <v-icon color="white" class="mr-2">mdi-calendar-alert</v-icon>
          <span class="text-h6 font-weight-semibold" style="color: white;">
            {{ $t('office.existing-reservation') }}
          </span>
          <v-spacer />
          <v-btn icon variant="text" color="white" @click="showExistingDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4" style="max-height: 400px;">
          <v-row dense>
            <v-col
              v-for="(res, index) in existingReservations"
              :key="index"
              cols="12"
            >
              <v-card
                rounded="lg"
                elevation="0"
                class="pa-3"
                style="background: #FFF5F5; border: 1px solid #FEB2B2;"
              >
                <div class="d-flex align-center">
                  <v-icon color="#DC3545" class="mr-2">mdi-clock-alert</v-icon>
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-medium">
                      {{ moment(res.startTime).format('MMM DD, YYYY HH:mm') }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ $t('booking.to') }} {{ moment(res.endTime).format('HH:mm') }}
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            variant="flat"
            color="#285096"
            rounded="lg"
            class="text-none"
            @click="showExistingDialog = false"
          >
            {{ $t('booking.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      rounded="lg"
      location="bottom right"
    >
      <div class="d-flex align-center ga-2 font-weight-medium">
        <v-icon size="20">{{ snackbar.icon }}</v-icon>
        {{ snackbar.message }}
      </div>
      <template #actions>
        <v-btn variant="text" class="text-none" @click="snackbar.show = false">
          {{ $t('booking.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Loader from '@/components/general/Loader.vue'
import { DatePicker } from 'v-calendar'
import moment from 'moment'
import { useOAuthService } from '@/services/OAuthService'
import {
  getReservationsByPoi,
  getPoiCategoryById,
  getFloorById,
  getAppointmentById,
  getBuildings,
  getPoiById,
  getReservationById,
  createReservation,
  createAppointment,
  updateReservation,
  updateAppointment,
  deleteReservation,
} from '@/controllers/BaseController'

export default {
  name: 'CreateEditBooking',
  components: {
    Loader,
    DatePicker,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { t } = useI18n()
    const oAuthService = useOAuthService()

    const formRef = ref(null)
    const showLoader = ref(false)
    const isSaving = ref(false)
    const datePickerDialog = ref(false)
    const showExistingDialog = ref(false)
    const showDatePicker = ref(true)
    const timeError = ref('')
    const clickedDateRange = ref('')

    const reservation = ref({
      id: '',
      title: '',
      creatorName: '',
      poi: { id: '', name: '' },
      category: { id: '', name: '' },
      floor: { id: '', name: '' },
      building: { id: '', name: '' },
      creatorEmail: '',
      campusId: '',
      personCount: 1,
      description: '',
      range: { 
        start: new Date(), 
        end: moment().add(1, 'hour').toDate() 
      },
    })

    const tempRange = ref({ ...reservation.value.range })
    const existingReservations = ref([])
    const originalReservation = ref(null)
    let originalEndTime = null

    const disabledDates = ref([
      {
        repeat: {
          weekdays: [1, 7], // Sunday=1, Saturday=7
        },
      },
    ])

    const snackbar = reactive({
      show: false,
      message: '',
      color: 'success',
      icon: 'mdi-check-circle'
    })

    const rules = {
      required: v => !!v || t('booking.field-required'),
      minLength: v => (v && v.length >= 3) || t('booking.min-3-chars'),
    }

    const timeRules = computed(() => [{
      hours: (hour) => hour >= 6 && hour <= 20,
      minutes: (minute, { hours }) => {
        if (hours === 20) return minute === 0
        return minute % 15 === 0
      },
    }])

    const resolveBookingDate = (...candidates) => {
      for (const value of candidates) {
        if (!value) continue
        const parsed = moment(value)
        if (parsed.isValid()) {
          return parsed.toDate()
        }
      }
      return null
    }

    const isNew = computed(() => !route.params?.reservation_id)

    const isBatchReservation = computed(() => {
      const start = moment(tempRange.value.start)
      const end = moment(tempRange.value.end)
      return !start.isSame(end, 'day')
    })

    const isRangeEqual = computed(() => 
      moment(tempRange.value.start).isSame(tempRange.value.end, 'minute')
    )

    const hasChanges = computed(() => {
      if (!originalReservation.value) return false
      return JSON.stringify(reservation.value) !== JSON.stringify(originalReservation.value)
    })

    const canSubmit = computed(() => {
      const titleValid = reservation.value.title.trim().length >= 3
      const requiredValid = reservation.value.poi && reservation.value.category
      const editValid = isNew.value || hasChanges.value
      const noConflicts = existingReservations.value.length === 0
      return titleValid && requiredValid && editValid && noConflicts
    })

    const showNotification = (message, type = 'success') => {
      snackbar.message = message
      snackbar.color = type === 'success' ? '#28A745' : type === 'error' ? '#DC3545' : '#23A5E6'
      snackbar.icon = type === 'success' ? 'mdi-check-circle' : type === 'error' ? 'mdi-alert-circle' : 'mdi-information'
      snackbar.show = true
    }

    const onDayClick = (day) => {
      let start = day.startDate
      let end = day.endDate
      clickedDateRange.value = { start: start, end: end }
    }

    const validateTimeRange = async (range) => {
      console.log('Validating time range:', range)
      const start = moment(range.start)
      const end = moment(range.end)

      let adjustedRange = { ...range }

      // Prevent start time from being 20
      if (start.hour() === 20) {
        console.error("Start time cannot be 20.")
        timeError.value = t('booking.start-time-error')
        adjustedRange.start = start.clone().set({ hour: 19, minute: 45 }).toDate()
        adjustedRange.end = start.clone().add(15, 'minutes').toDate()
      } else {
        timeError.value = ''
      }

      if (end.isSameOrBefore(start)) {
        adjustedRange.end = start.clone().add(15, 'minutes').toDate()
      } else if (start.isSame(end)) {
        adjustedRange.end = start.clone().add(15, 'minutes').toDate()
      }

      if (end.hour() === 20 && end.minute() > 0) {
        adjustedRange.end = end.clone().startOf('hour').toDate()
      }

      showDatePicker.value = false

      console.log('Adjusted Range:', adjustedRange.start)
      console.log('Adjusted Range:', adjustedRange.end)

      range.start = adjustedRange.start
      range.end = adjustedRange.end
      tempRange.value.start = adjustedRange.start
      tempRange.value.end = adjustedRange.end
      await nextTick()
      showDatePicker.value = true
    }

    const checkReservations = async () => {
      try {
        showLoader.value = true
        const start = moment(reservation.value.range.start).toDate()
        const end = moment(reservation.value.range.end).toDate()
        const reservations = await getReservationsByPoi(
          reservation.value.poi.id,
          start,
          end
        )
        return reservations || []
      } catch (error) {
        console.error('Error checking reservations:', error)
        return []
      } finally {
        showLoader.value = false
      }
    }

    const discardRange = () => {
      tempRange.value = { ...reservation.value.range }
      datePickerDialog.value = false
      timeError.value = ''
    }

    const confirmRange = async () => {
      let clickDateRange = JSON.parse(JSON.stringify(clickedDateRange.value))
      if (clickDateRange !== '') {
        const clickedEndDate = moment(clickDateRange.end).startOf('day')

        if (!clickedEndDate.isSame(moment(tempRange.value.end).startOf('day'))) {
          tempRange.value.start = moment(tempRange.value.start)
            .set({ year: clickedEndDate.year(), month: clickedEndDate.month(), date: clickedEndDate.date() })
            .toDate()

          tempRange.value.end = moment(tempRange.value.end)
            .set({ year: clickedEndDate.year(), month: clickedEndDate.month(), date: clickedEndDate.date() })
            .toDate()
        }
      }

      reservation.value.range = { ...tempRange.value }
      datePickerDialog.value = false
      
      let reservations = await checkReservations()
      existingReservations.value = []
      existingReservations.value = reservations
      
      if (reservations.length > 0) {
        showNotification(t('booking.time-slot-occupied'), 'error')
      }
    }

    const submitBooking = async () => {
      const { valid } = await formRef.value.validate()
      if (!valid) return

      isSaving.value = true
      let reservationResponse = null
      let appointmentResponse = null
      let isOutlookError = false

      try {
        const payload = {
          title: reservation.value.title,
          poiId: reservation.value.poi.id,
          startTime: tempRange.value.start.toISOString(),
          endTime: tempRange.value.end.toISOString(),
          personCount: reservation.value.personCount,
          creatorEmail: reservation.value.creatorEmail,
          creatorName: reservation.value.creatorName,
          poiName: reservation.value.poi.name,
          poiCategory: reservation.value.category.name,
          floorNumber: reservation.value.floor.number,
          floorName: reservation.value.floor.name,
          buildingName: reservation.value.building.name,
        }

        if (isBatchReservation.value) {
          originalEndTime = tempRange.value.end
          let batchEndDate = moment(tempRange.value.start)
            .hour(moment(originalEndTime).hour())
            .minute(moment(originalEndTime).minute())
          payload.endTime = batchEndDate.toISOString()

          originalEndTime = moment(originalEndTime).endOf('day')
          let formattedUntilDate = originalEndTime.toISOString().split('.')[0] // Removing microseconds part

          payload.recurrence = {
            until: formattedUntilDate,
            frequency: 'WEEKDAY',
          }
        }

        reservationResponse = await createReservation(payload)
        let reservationId = reservationResponse['id']

        const appointmentPayload = {
          poiId: reservation.value.poi.id,
          title: reservation.value.title,
          moderatorEmail: reservation.value.creatorEmail,
          startTime: tempRange.value.start.toISOString(),
          endTime: tempRange.value.end.toISOString(),
          reservationId: reservationId,
          customerId: reservationResponse.customerId,
          creatorEmail: reservation.value.creatorEmail,
          organizerName: reservation.value.creatorName,
          location: reservation.value.poi.name,
          campusId: reservation.value.campusId,
        }

        if (isBatchReservation.value) {
          let originalEnd = tempRange.value.end
          let batchEndDate = moment(tempRange.value.start)
            .hour(moment(originalEnd).hour())
            .minute(moment(originalEnd).minute())
          appointmentPayload['endTime'] = batchEndDate.toISOString()

          originalEnd = moment(originalEnd).endOf('day')
          let formattedUntilDate = originalEnd.toISOString().split('.')[0] // Removing microseconds part

          appointmentPayload['recurrence'] = {
            until: formattedUntilDate,
            frequency: 'WEEKDAY',
          }
          appointmentPayload['reservationRecurringId'] = reservationResponse['recurringId']
        }

        if (reservation.value.description !== '') {
          appointmentPayload['description'] = reservation.value.description
        }

        appointmentResponse = await createAppointment(appointmentPayload)
          .then(response => response)
          .catch(e => {
            let errorMessage = e?.response?.data?.errors[0]?.message
            if (errorMessage?.includes('resources') && errorMessage?.includes('time range')) {
              isOutlookError = true
            }
            throw new Error(errorMessage)
          })

        if (reservationResponse?.id && appointmentResponse?.id) {
          showNotification(t('messages.booking-successful'))
          setTimeout(() => {
            router.push({ name: 'UserBookings' })
          }, 1500)
        }

      } catch (error) {
        console.error('Booking error:', error)
        
        if (reservationResponse && !appointmentResponse) {
          try {
            await deleteReservation(reservationResponse.id)
          } catch (deleteError) {
            console.error('Failed to rollback reservation:', deleteError)
          }
        }

        showNotification(
          isOutlookError 
            ? t('messages.room-occupied')
            : t('messages.booking-create-error-text'),
          'error'
        )
      } finally {
        isSaving.value = false
      }
    }

    const editBooking = async () => {
      const { valid } = await formRef.value.validate()
      if (!valid) return

      isSaving.value = true
      const maxRetries = 2
      let retryCount = 0
      let successful = false
      let RESERVATION = JSON.parse(JSON.stringify(reservation.value))
      let reservationId = RESERVATION.id

      while (retryCount < maxRetries && !successful) {
        try {
          let currentReservation = await getReservationById(reservationId)
          let reservationsPayload = {
            title: RESERVATION.title,
            poiId: RESERVATION.poi.id,
            startTime: moment(RESERVATION.range.start).toISOString(),
            endTime: moment(RESERVATION.range.end).toISOString(),
            personCount: RESERVATION.personCount,
          }

          // Update the reservation
          let updateResponse = await updateReservation(reservationId, reservationsPayload)
          console.log('updateResponse', updateResponse)
          reservationId = updateResponse.id // Assuming the updated reservation ID is returned
          successful = true

          // Prepare payload for updating appointment
          let APPOINTMENT = await getAppointmentById(currentReservation.appointment.id)
          let appointmentsPayload = {
            title: RESERVATION.title,
            startTime: moment(RESERVATION.range.start).toISOString(),
            endTime: moment(RESERVATION.range.end).toISOString(),
            reservationId: reservationId,
            description: RESERVATION.description,
            invitees: RESERVATION.invitees || [],
          }

          // Update the appointment
          console.log('Updating appointment', appointmentsPayload)
          await updateAppointment(APPOINTMENT?.id, appointmentsPayload)

          showNotification(t('messages.booking-update-successful'))
          setTimeout(() => {
            router.push({ name: 'UserBookings' })
          }, 1500)

        } catch (error) {
          console.error('Update error:', error)
          if (error.response?.data?.errors.some(e => e.message.includes('ERR_VERSION_CONFLICT'))) {
            retryCount++
            if (retryCount >= maxRetries) {
              showNotification(t('messages.booking-update-error'), 'error')
              break
            }
          } else {
            showNotification(t('messages.booking-update-error'), 'error')
            break
          }
        }
      }

      isSaving.value = false
    }

    const goBack = () => {
      router.go(-1)
    }

    onMounted(async () => {
      try {
        showLoader.value = true
        
        const user = await oAuthService.getUserProfile()
        reservation.value.creatorName = user.name || user.displayName
        reservation.value.creatorEmail = user.email

        const params = route.params
        const buildings = await getBuildings()
        const building = buildings.find(b => b.id === params.building_id)
        
        reservation.value.campusId = building?.campus?.id
        reservation.value.building = { 
          id: params.building_id, 
          name: building?.name 
        }

        const [poi, category] = await Promise.all([
          getPoiById(params.poi_id),
          getPoiCategoryById(params.category_id)
        ])

        const floor = await getFloorById(poi.floors.ids[0])

        reservation.value.poi = { id: params.poi_id, name: poi.name }
        reservation.value.category = { id: params.category_id, name: category.name }
        reservation.value.floor = { id: floor.id, name: floor.name, number: floor.number }
        reservation.value.title = `${poi.name} ${t('booking.reservation')}`

        if (isNew.value) {
          const conflicts = await checkReservations()
          existingReservations.value = conflicts
        } else {
          const userBooking = await getReservationById(params.reservation_id)
          let appointment = null
          
          if (userBooking.appointment?.id) {
            appointment = await getAppointmentById(userBooking.appointment.id)
            reservation.value.title = appointment.title
            reservation.value.description = appointment.description
          }

          const resolvedStart = resolveBookingDate(userBooking.startTime, appointment?.startTime)
          const resolvedEnd = resolveBookingDate(userBooking.endTime, appointment?.endTime)

          reservation.value.id = userBooking.id
          reservation.value.personCount = userBooking.personCount
          reservation.value.range = {
            start: resolvedStart ?? new Date(),
            end: resolvedEnd ?? moment(resolvedStart ?? new Date()).add(1, 'hour').toDate()
          }
          tempRange.value = { ...reservation.value.range }
          originalReservation.value = JSON.parse(JSON.stringify(reservation.value))
        }
      } catch (error) {
        console.error('Initialization error:', error)
        showNotification(t('messages.loading-error'), 'error')
      } finally {
        showLoader.value = false
      }
    })

    return {
      formRef,
      showLoader,
      isSaving,
      datePickerDialog,
      showExistingDialog,
      showDatePicker,
      timeError,
      reservation,
      tempRange,
      existingReservations,
      disabledDates,
      snackbar,
      rules,
      timeRules,
      isNew,
      isBatchReservation,
      isRangeEqual,
      canSubmit,
      onDayClick,
      validateTimeRange,
      discardRange,
      confirmRange,
      submitBooking,
      editBooking,
      goBack,
      moment,
    }
  },
}
</script>

<style scoped>
.create-booking-container {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);
}

/* Info Card Styles */
.info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.info-content {
  flex: 1;
}

.info-label {
  font-size: 11px;
  color: #9AA6B1;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.info-value {
  font-size: 14px;
  color: #285096;
  font-weight: 600;
  line-height: 1.3;
}

/* Date Time Card */
.date-time-card {
  background: linear-gradient(135deg, #FFFFFF 0%, #F3F6FF 100%);
  border: 2px solid #E2E8F0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 20px;
}

.date-time-card:hover {
  border-color: #23A5E6;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.12);
}

.date-time-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.date-time-label {
  font-size: 16px;
  font-weight: 600;
  color: #285096;
}

.date-time-content {
  position: relative;
}

.date-time-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.date-time-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 200px;
}

.date-time-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.date-time-icon-wrapper.start {
  background: rgba(52, 211, 153, 0.15);
  color: #10B981;
}

.date-time-icon-wrapper.end {
  background: rgba(35, 165, 230, 0.15);
  color: #23A5E6;
}

.date-time-info {
  flex: 1;
}

.date-time-sub-label {
  font-size: 11px;
  color: #9AA6B1;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.date-time-value {
  font-size: 15px;
  color: #001437;
  font-weight: 600;
  line-height: 1.3;
}

.date-time-sub-value {
  font-size: 20px;
  color: #285096;
  font-weight: 700;
  margin-top: 2px;
}

.date-arrow {
  flex-shrink: 0;
}

.batch-indicator {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #E2E8F0;
}

/* Readonly Field */
.readonly-field :deep(.v-field) {
  background: #F8FAFC;
}

/* Date Picker Dialog */
.date-picker-dialog-card {
  overflow: hidden;
}

.date-picker-wrapper {
  padding: 20px;
  display: flex;
  justify-content: center;
  background: #FFFFFF;
}

.date-picker-wrapper :deep(.vc-container) {
  border: none;
  box-shadow: none;
  font-family: 'Inter', sans-serif;
}

/* Selected date range styling */
.date-picker-wrapper :deep(.vc-day.is-selected) {
  background-color: #23A5E6 !important;
  color: white !important;
}

.date-picker-wrapper :deep(.vc-highlight) {
  background-color: #23A5E6 !important;
  opacity: 1 !important;
}

.date-picker-wrapper :deep(.vc-highlight-bg-solid) {
  background-color: #23A5E6 !important;
}

.date-picker-wrapper :deep(.vc-day-content.is-selected) {
  background-color: #23A5E6 !important;
  color: white !important;
  font-weight: 600;
}

/* Range selection styling */
.date-picker-wrapper :deep(.vc-day.in-range) {
  background-color: rgba(35, 165, 230, 0.2) !important;
}

.date-picker-wrapper :deep(.vc-day.in-range .vc-day-content) {
  color: #285096 !important;
  font-weight: 500;
}

/* Start and end of range */
.date-picker-wrapper :deep(.vc-day.on-left),
.date-picker-wrapper :deep(.vc-day.on-right) {
  background-color: #23A5E6 !important;
}

.date-picker-wrapper :deep(.vc-day.on-left .vc-day-content),
.date-picker-wrapper :deep(.vc-day.on-right .vc-day-content) {
  background-color: #23A5E6 !important;
  color: white !important;
  font-weight: 700;
}

/* Hover states */
.date-picker-wrapper :deep(.vc-day:not(.is-disabled):hover .vc-day-content) {
  background-color: rgba(35, 165, 230, 0.3) !important;
}

.date-picker-wrapper :deep(.vc-day.is-selected:hover .vc-day-content) {
  background-color: #1E8FCC !important;
}

/* Disabled dates (weekends) styling */
.date-picker-wrapper :deep(.vc-day.is-disabled) {
  opacity: 0.4;
  cursor: not-allowed;
}

.date-picker-wrapper :deep(.vc-day.is-disabled .vc-day-content) {
  color: #CBD5E0 !important;
  background-color: #F7FAFC !important;
  cursor: not-allowed;
}

.date-picker-wrapper :deep(.vc-day.is-disabled:hover .vc-day-content) {
  background-color: #F7FAFC !important;
  transform: none;
}

.date-picker-wrapper :deep(.vc-day.is-disabled .vc-highlights) {
  display: none;
}

/* Time picker styling */
.date-picker-wrapper :deep(.vc-time-picker) {
  background-color: #F8FAFC;
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
}

.date-picker-wrapper :deep(.vc-time-select) {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  color: #285096;
  font-weight: 500;
}

.date-picker-wrapper :deep(.vc-time-select:focus) {
  border-color: #23A5E6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(35, 165, 230, 0.1);
}

/* Animation for cards */
.v-card {
  animation: fadeInUp 0.4s ease-out backwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .date-time-row {
    flex-direction: column;
    align-items: stretch;
  }

  .date-arrow {
    transform: rotate(90deg);
    margin: 8px 0;
  }

  .date-time-item {
    min-width: 100%;
  }

  /* Reduce spacing on mobile */
  .create-booking-container :deep(.v-container) {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }

  .create-booking-container :deep(.v-sheet.mt-4) {
    margin-top: 8px !important;
  }

  .create-booking-container :deep(.v-card) {
    padding: 12px !important;
  }

  .create-booking-container :deep(.v-card .pa-4) {
    padding: 12px !important;
  }

  .create-booking-container :deep(.v-card .pa-md-6) {
    padding: 12px !important;
  }

  .create-booking-container :deep(.v-row) {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  .create-booking-container :deep(.v-col) {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
  }

  /* Reduce info card padding */
  .info-item {
    margin-bottom: 12px;
  }

  /* Reduce date-time card padding */
  .date-time-card {
    padding: 12px !important;
  }

  .date-time-header {
    margin-bottom: 12px;
  }

  /* Reduce form field spacing */
  .create-booking-container :deep(.v-text-field) {
    margin-bottom: 0 !important;
  }

  .create-booking-container :deep(.v-textarea) {
    margin-bottom: 0 !important;
  }

  /* Alert compact */
  .create-booking-container :deep(.v-alert) {
    padding: 12px !important;
    margin-bottom: 8px !important;
  }
}

@media (max-width: 600px) {
  .info-item {
    margin-bottom: 8px;
  }

  /* Even tighter spacing for small phones */
  .create-booking-container :deep(.v-container) {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .create-booking-container :deep(.v-col) {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .date-time-card {
    padding: 10px !important;
  }

  .date-time-icon-wrapper {
    width: 36px;
    height: 36px;
  }

  .date-time-icon-wrapper .v-icon {
    font-size: 14px !important;
  }

  /* Compact header on mobile */
  .create-booking-container :deep(.px-4) {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .create-booking-container :deep(.py-4) {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
}
</style>
